<template>
  <v-overlay
    color="grey900" opacity="1" :value="active" class="spinner-overlay"
    absolute
  >
    <div v-if="game.status === GAME_STATUS.open" class="text--disabled text-center">
      Waiting for players
    </div>

    <div v-else-if="game.status === GAME_STATUS.locked" class="d-flex justify-center">
      <div v-if="countdownFinished || countdownTime < 0" class="d-flex align-center">
        <fai :icon="['fad', 'file-contract']" class="mr-4 green400--text text-h2" />
        <div class="text-uppercase">
          <h5 v-t="'page.wheel.status.awaiting'" class="text-body-2 lh-1-2" />
          <h5 v-t="'common.eos_block'" class="text-body-2 lh-1-3 mb-1" />
          <a
            class="link d-block lh-1 text-body-1 blue300--text"
            rel="noopener noreferrer"
            target="_blank"
            :href="`https://bloks.io/block/${game.fairness.solvingBlockId}`"
          >
            #{{ game.fairness.solvingBlockId }}
          </a>
        </div>
      </div>

      <div v-else class="text-center">
        <h4 class="text-h6 text-uppercase text--secondary">
          Game starting
        </h4>
        <Countdown
          class="text-h3 font-weight-bold"
          :duration="countdownTime / 1000"
          @finish="countdownFinished = true"
        />
      </div>
    </div>

    <div v-else-if="game.status === GAME_STATUS.complete">
      <v-container class="winner-tally">
        <v-row dense>
          <v-col
            v-for="team in game.teams" :key="team.id"
            :cols="12 * (team.slots / game.config.slots)"
            class="d-flex justify-center"
          >
            <h4
              class="team-total d-inline-block text-h4 font-weight-bold"
              :class="[`team-id-${team.id}`, {'is-winner': winningTeams.includes(team.id)}]"
            >
              <fai
                v-if="game.isCursed"
                :icon="['fad', 'skull-crossbones']"
                class="cursed-tally text-h5 primary500--text mr-3"
              />
              <span />
            </h4>
          </v-col>
        </v-row>
      </v-container>

      <div class="game-total text-h4">
        <scrap :amount="game.totalWon" />
      </div>
    </div>
  </v-overlay>
</template>

<script>
import confetti from 'canvas-confetti'
import anime from 'animejs/lib/anime.es'
import seededRandom from 'seed-random'

import { registerSound } from '@/modules/sound'
import { getRandomIntInRange } from '@/utils'
import { GAME_STATUS } from '../../utils'

const sounds = {
  tally: [
    registerSound('games/case-battles/tally1', { volume: 0.25 }),
    registerSound('games/case-battles/tally2', { volume: 0.25 }),
    registerSound('games/case-battles/tally3', { volume: 0.25 }),
  ],
  win: [
    registerSound('games/case-battles/win1', { volume: 0.3 }),
    registerSound('games/case-battles/win2', { volume: 0.3 }),
    registerSound('games/case-battles/win3', { volume: 0.3 }),
  ],
  winBig: [
    registerSound('games/case-battles/jackpot1', { volume: 0.4 }),
    registerSound('games/case-battles/jackpot2', { volume: 0.4 }),
    registerSound('games/case-battles/jackpot3', { volume: 0.4 }),
  ],
  winUp: registerSound('games/case-battles/win-up', { volume: 0.4 }),
}


export default {
  props: {
    active: Boolean,
    game: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    muted: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    animation: null,
    countdownFinished: false,
  }),
  computed: {
    GAME_STATUS() {
      return GAME_STATUS
    },
    winningTeams() {
      return this.game.data.winningTeams ?? []
    },
    countdownTime() {
      return this.game.lockingAt - Date.now()
    },
  },
  beforeDestroy() {
    this.killAnim()
  },
  methods: {
    tallyWinners(timeSinceTally) {
      this.killAnim()

      const { winningTeams, muted } = this
      const {
        winners, id, rounds, isCursed, isCommunism, totalWon,
      } = this.game

      // shallow copy to animate
      const teamTotals = { ...this.game.teamTotals }

      const $overlay = this.$el
      const $tally = $overlay.querySelector('.winner-tally')
      const $confetti = this.$el.parentElement.querySelector('.confetti')
      $confetti.confetti = $confetti.confetti || confetti.create($confetti, { resize: true })

      const baseDuration = Math.min(4000, rounds.length * 300)

      const animatables = Object.entries(teamTotals).map(([teamId, total], i) => {
        const $el = $tally.querySelector(`.team-total.team-id-${teamId}`)
        const $inner = $el.querySelector('span')
        const rand = seededRandom(`${id}-${i}`)()
        const easing = ['easeOutElastic', 'easeOutBounce', 'easeInOutCirc'][Math.floor(rand * (0 - 3) + 3)]
        const duration = rand * (500 - 1500) + 1500

        return {
          $el,
          $inner,
          total,
          easing,
          duration: baseDuration + duration,
        }
      })

      let lastTick = 0
      this.animation = anime
        .timeline()
        .add({
          targets: animatables.map(i => i.$inner),
          innerHTML(el, i) {
            return isCursed
              ? [totalWon / 100, animatables[i].total / 100]
              : [0, animatables[i].total / 100]
          },
          round: 100,
          easing: (el, i) => animatables[i].easing,
          duration: (el, i) => animatables[i].duration,
          update(anim) {
            // use cached
            if (muted) return
            if (anim.progress > 90) return

            const now = Date.now()

            if (now > lastTick + 100) {
              sounds.tally[getRandomIntInRange(0, 2)].play()
              lastTick = now
            }
          },
        })

      if (!isCommunism) {
        this.animation.add({
          targets: $tally.querySelectorAll('.team-total.is-winner'),
          translateY: [0, -30],
          scale: [1, 1.2],
          ...(isCursed && {
            color: [
              this.$vuetify.theme.themes.dark.grey100,
              this.$vuetify.theme.themes.dark.primary500,
            ],
          }),
          easing: 'easeOutCubic',
          duration: 400,
          delay: anime.stagger(250),
          endDelay: 400,
          begin: () => {
            if (this.muted) return

            for (const [i] of winningTeams.entries()) {
              setTimeout(() => sounds.winUp.play(), 250 * i)
            }
          },
        })
          .add({
            targets: $tally.querySelectorAll('.team-total:not(.is-winner)'),
            scale: [1, 0.8],
            opacity: [1, 0.5],
            easing: 'easeOutCubic',
            duration: 400,
          }, '-=400')
      }

      this.animation.add({
        targets: animatables.map(i => i.$el),
        translateY: -200,
        easing: 'easeInOutCirc',
        delay: anime.stagger(100),
        duration: 800,
      }, '-=300')
        .add({
          targets: $overlay.querySelector('.game-total .scrap'),
          translateY: [200, 0],
          opacity: [0, 1],
          rotate: [45, 0],
          scale: [{ value: 0.4, duration: 600 }, { value: 1.3, duration: 600, easing: 'easeOutBack' }],
          easing: 'easeInOutCirc',
          duration: 800,
          begin: () => {
            if (this.muted) return

            setTimeout(() => {
              const perTeam = this.game.totalWon / winners.length
              if (perTeam > this.game.price * 3) {
                sounds.winBig[getRandomIntInRange(0, 2)].play()
              } else {
                sounds.win[getRandomIntInRange(0, 2)].play()
              }

              $confetti.confetti({ spread: getRandomIntInRange(50, 120), origin: { y: 1.5 } })
              $confetti.confetti({ spread: 70, angle: 60, origin: { x: 0, y: 1 } })
              $confetti.confetti({ angle: 120, spread: 70, origin: { x: 1, y: 1 } })
            }, 600)
          },
        }, '-=800')
        .add({
          targets: document.querySelectorAll('.team-header .team-slot .won-amount > span'),
          translateY: ['80%', 0],
          opacity: [0, 1],
          easing: 'easeOutCirc',
          delay: anime.stagger(100, { from: 'center' }),
          duration: 800,
        }, '-=300')


      // don't seek for no reason
      if (timeSinceTally > 250) {
        this.animation.seek(Math.min(this.animation.duration, timeSinceTally))
      }

      if (timeSinceTally < this.animation.duration) {
        this.animation.play()
      }

      return this.animation
    },
    killAnim() {
      if (!this.animation) return

      this.animation.pause()
      this.animation.animatables.forEach(({ target }) => {
        anime.remove(target)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.spinner-overlay {
  overflow: hidden;
  z-index: 3 !important;

  ::v-deep .v-overlay__scrim {
    background: url('../../../../../assets/img/games/wheel/background.jpg') no-repeat center center;
    background-size: cover;

    &::before {
      opacity: 0.8;
      background: grey(900);
    }
  }

  ::v-deep .v-overlay__content {
    width: 100%;
  }

  .team-total .cursed-tally {
    position: absolute;
    top: 50%;
    left: -12px;
    transform: translate(-100%, -50%);
  }

  .winner-tally {
    .team-total {
      min-width: 100px;
    }
  }

  .game-total {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }
}

</style>
